/**
 * roro-cart-reward — el aviso de «ya tienes la oferta».
 *
 * QUÉ PROBLEMA RESUELVE
 *
 * La escalera de `snippets/roro-cart-shipping-bar.liquid` dice el ESTADO
 * («знижка 5% застосована»), pero no dice que ACABA de pasar. Y no puede: se
 * re-renderiza en el servidor en cada `cart:update`, así que cuando el cliente
 * abre el cajón la barra ya está llena y el texto ya está cambiado. Nunca ve la
 * transición, que es justo lo que se celebra.
 *
 * Este archivo pone el momento. Nada más: no aplica códigos —eso es
 * `roro-cart-bundle-discount.js`—, no calcula rebajas y no pinta ni un precio.
 *
 * DÓNDE SALE, Y POR QUÉ NO SIEMPRE EN EL MISMO SITIO
 *
 * En este tema `CartAddEvent.eventName === ThemeEvents.cartUpdate === 'cart:update'`
 * (`assets/events.js`), y `<cart-drawer-component>` escucha ese mismo nombre con
 * `auto-open` puesto (`assets/cart-drawer.js`, `settings.auto_open_cart_drawer`).
 * O sea: CUALQUIER cambio del carrito abre el cajón. Un aviso flotante sobre la
 * página no solo sería redundante — es que quedaría DETRÁS del `<dialog>`, que
 * vive en el top layer, y no se vería nunca.
 *
 * Por eso el aviso es UNO y tiene DOS casas:
 *
 *   · cajón abierto (el caso normal) -> se mete DENTRO del `<dialog>`, encima de
 *     la escalera. Aparece, se lee, se va, y al irse descubre la escalera ya
 *     completa. Sin capa nueva sobre la página y sin tapar nada de forma
 *     permanente.
 *   · cajón cerrado (`auto_open_cart_drawer` apagado, o el cajón se cerró antes
 *     de que llegase un `cart:update` tardío) -> nube anclada al icono del
 *     carrito del header, que es sticky y está en todas las páginas.
 *
 * Se cuelga del `<dialog>` y NO de `.cart-drawer__inner`: ese div es
 * `data-hydration-key="cart-drawer-inner"`, o sea la raíz que morfea
 * `cart-items-component` en cada `cart:update`. Cualquier nodo o clase que
 * pongamos ahí dentro desaparece en el siguiente repintado — y llega uno ~700 ms
 * después, cuando `roro-cart-bundle-discount.js` pega el código.
 *
 * NO SE CELEBRA EN LA CARGA
 *
 * El estado de partida lo escribe Liquid en el `<script>` (`-disc-met`,
 * `-ship-met`), calculado en el servidor con el carrito real. Aquí solo se
 * anuncian TRANSICIONES de false a true durante la vida de la página. Entrar en
 * la web con un carrito que ya pasaba de ₴3.000 no dispara nada: eso no es una
 * noticia, es un estado, y ya lo cuenta la escalera.
 *
 * NO SE CALCULA NADA QUE NO SEA UN UMBRAL
 *
 * Los dos mínimos vienen de los MISMOS settings que la escalera y que
 * `roro-cart-bundle-discount.js` (`gift_pack_discount_min`,
 * `cart_free_shipping_threshold`), y se miden con el mismo total que ellos:
 *
 *   · descuento -> `original_total_price`, el subtotal ANTES de rebajas, que es
 *     lo que mira el `minimumRequirement` de Shopify. Con `total_price` un
 *     carrito de ₴3.100 caería a ₴2.945 al aplicarse el 5%, el umbral dejaría de
 *     cumplirse y el aviso se podría disparar en bucle.
 *   · envío -> `total_price`, que es lo que cobra `ro.ro pay`. Pide de más,
 *     nunca de menos.
 *
 * Si algún día los umbrales de aquí y los del Admin dejan de decir lo mismo, el
 * aviso promete algo que el checkout no cumple. Es el incidente de agosto de
 * 2026 otra vez: los cuatro sitios se cambian el mismo día (`rules/descuentos.md`).
 */
(function () {
  'use strict';

  var config = document.querySelector('script[data-roro-reward-config]');
  var toast = document.querySelector('[data-roro-reward]');
  if (!config || !toast) return;

  var DISC_MIN = parseInt(config.getAttribute('data-roro-reward-disc-min'), 10) || 0;
  var SHIP_MIN = parseInt(config.getAttribute('data-roro-reward-ship-min'), 10) || 0;
  if (!DISC_MIN && !SHIP_MIN) return;
  // Piezas que pide el −5% además del importe (2026-10-08): una pieza sola no.
  var DISC_MIN_ITEMS = parseInt(config.getAttribute('data-roro-reward-disc-min-items'), 10);
  if (!(DISC_MIN_ITEMS >= 1)) DISC_MIN_ITEMS = 2;
  // Solo para anticipar lo que se pagará mientras el código aún no está pegado.
  var DISC_PCT = parseFloat(config.getAttribute('data-roro-reward-disc-pct')) || 0;

  var textEl = toast.querySelector('[data-roro-reward-text]');
  var ctaEl = toast.querySelector('[data-roro-reward-open]');
  if (!textEl) return;

  var MSG = {
    disc: config.getAttribute('data-roro-reward-msg-disc') || '',
    ship: config.getAttribute('data-roro-reward-msg-ship') || ''
  };

  // Dónde vivía el aviso en el marcado. Se guarda antes de moverlo a ningún
  // sitio para poder devolverlo siempre al mismo punto del header.
  var home = toast.parentNode;

  // Cuánto se queda en pantalla. Es un aviso, no un panel: se lee de un vistazo
  // y se va solo. Nadie tiene que cerrarlo.
  var SHOW_MS = 4000;

  // Cuánto se espera antes de decidir dónde pintarlo. Tiene que cubrir DOS cosas
  // que pasan después del `cart:update`: el morphing de `cart-items-component`
  // (que puede ir por Section Rendering API) y el `showModal()` del cajón, que
  // `assets/dialog.js` hace dentro de un `requestAnimationFrame`. Antes de eso,
  // `dialog.open` todavía dice `false` y el aviso saldría en la casa equivocada.
  // De paso evita que la nube salte en el mismo frame que el toque, que se lee
  // como un parpadeo.
  var SETTLE_MS = 420;

  var met = {
    disc: config.getAttribute('data-roro-reward-disc-met') === 'true',
    ship: config.getAttribute('data-roro-reward-ship-met') === 'true'
  };

  var hideTimer = null;
  var settleTimer = null;
  var busy = false;

  function drawerDialog() {
    return document.querySelector('.cart-drawer__dialog');
  }

  /* ==================================================================== */
  /* Pintar y esconder                                                    */
  /* ==================================================================== */

  function hide() {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
    if (toast.hidden) return;

    toast.classList.remove('is-in');
    toast.classList.add('is-out');

    // El nodo se esconde de verdad al acabar la animación, pero con un
    // `setTimeout` y no con `animationend`: en una pestaña en segundo plano ese
    // evento no llega nunca y el aviso se quedaría colgado y enfocable.
    setTimeout(function () {
      toast.classList.remove('is-out');
      toast.hidden = true;
      // Se devuelve a su sitio del header para que el siguiente aviso no herede
      // la posición del cajón ni se vaya con él al cerrarse.
      if (home && toast.parentNode !== home) home.appendChild(toast);
    }, 260);
  }

  /**
   * Dentro del cajón: encima de la escalera, del ancho del cajón menos los
   * márgenes. No hay cola — no hay a qué apuntar, ya está donde tiene que estar.
   *
   * @param {HTMLElement} dialog
   */
  function placeInDrawer(dialog) {
    toast.setAttribute('data-roro-reward-place', 'drawer');
    if (toast.parentNode !== dialog) dialog.appendChild(toast);

    toast.style.right = '';
    toast.style.removeProperty('--roro-reward-tail');

    // Se mide la escalera en vez de cablear un número: encima de ella puede
    // haber la banda de `settings.cart_announcement_text`, que aparece y
    // desaparece desde el theme editor.
    var ladder = dialog.querySelector('[data-roro-ladder]');
    var dialogBox = dialog.getBoundingClientRect();
    var top = 64;
    if (ladder) top = ladder.getBoundingClientRect().top - dialogBox.top;

    // Se ACOTA por arriba y por abajo. Sin el tope de abajo, un cajon que
    // todavia se esta abriendo (o una escalera que aun no ha terminado de
    // repintarse tras el morphing) puede devolver una posicion fuera de la
    // pantalla, y el aviso «no sale» aunque este en el DOM. 140px de margen
    // inferior es lo que ocupa el propio aviso mas aire.
    var maxTop = Math.max(8, Math.round(dialogBox.height - 140));
    toast.style.top = Math.min(maxTop, Math.max(8, Math.round(top))) + 'px';

    // Dentro del cajón «ver el carrito» no lleva a ningún sitio. Se quita del
    // DOM accesible, no solo de la vista, para que no quede en el tabulador.
    if (ctaEl) ctaEl.hidden = true;
  }

  /**
   * Fuera del cajón: nube colgando del icono del carrito, con la cola apuntando
   * a su centro. El header es `position: sticky` (`sections/roro-navbar.liquid`),
   * así que el ancla no se va con el scroll.
   */
  function placeOnPage() {
    toast.setAttribute('data-roro-reward-place', 'page');
    if (toast.parentNode !== document.body) document.body.appendChild(toast);
    if (ctaEl) ctaEl.hidden = false;

    var anchor =
      document.querySelector('[data-testid="cart-drawer-trigger"]') ||
      document.querySelector('[data-roro-drawer-cart]') ||
      document.querySelector('.roro-navbar__cart .header-actions__action');

    if (!anchor) {
      // Sin ancla no se inventa una posición: arriba a la derecha y sin cola.
      toast.style.top = '12px';
      toast.style.right = '12px';
      toast.style.removeProperty('--roro-reward-tail');
      return;
    }

    var rect = anchor.getBoundingClientRect();
    var margin = 12;
    var right = Math.max(margin, Math.round(window.innerWidth - rect.right - 4));

    toast.style.top = Math.round(rect.bottom + 10) + 'px';
    toast.style.right = right + 'px';

    // La cola se coloca DESPUÉS de posicionar la nube, porque se mide contra su
    // borde derecho ya colocado.
    var width = toast.getBoundingClientRect().width;
    var iconCenterFromRight = window.innerWidth - rect.right + rect.width / 2;
    var tail = iconCenterFromRight - right;
    tail = Math.max(16, Math.min(width - 16, tail));
    toast.style.setProperty('--roro-reward-tail', Math.round(tail) + 'px');
  }

  /**
   * @param {string} key - 'disc' o 'ship'.
   */
  function show(key) {
    var message = MSG[key];
    if (!message) return;

    textEl.textContent = message;

    // Los dos iconos viven en el marcado y se turnan. Nada de `innerHTML`: el
    // SVG lo pinta Liquid con `inline_asset_content` y aquí no se reescribe.
    var icons = toast.querySelectorAll('[data-roro-reward-icon]');
    for (var i = 0; i < icons.length; i++) {
      icons[i].hidden = icons[i].getAttribute('data-roro-reward-icon') !== key;
    }

    if (hideTimer) clearTimeout(hideTimer);
    toast.classList.remove('is-out');

    // Se enseña ANTES de medir: un nodo con `hidden` no tiene caja y la cola
    // saldría pegada al borde.
    toast.hidden = false;

    var dialog = drawerDialog();
    if (dialog && dialog.open) placeInDrawer(dialog);
    else placeOnPage();

    toast.classList.add('is-in');
    hideTimer = setTimeout(hide, SHOW_MS);
  }

  /* ==================================================================== */
  /* Cuándo hay algo que celebrar                                         */
  /* ==================================================================== */

  /**
   * @param {object} cart - Carrito completo de la Cart AJAX API.
   */
  function evaluate(cart) {
    if (!cart || typeof cart.total_price !== 'number') return;

    // El −5% sin tarjetas regalo, que Shopify no cuenta para su mínimo. El envío
    // sí las lleva: `ro.ro pay` las suma para el envío gratis (probado el 2026-10-06).
    // El carrito ligero del cajón (`data-roro-cart-state`) no trae líneas, por
    // eso trae el total de tarjetas ya sumado en `gift_card_total`.
    // Y las piezas, también sin tarjetas: el ligero trae `gift_card_units`.
    var discTotal = typeof cart.original_total_price === 'number' ? cart.original_total_price : cart.total_price;
    var discUnits = cart.item_count || 0;
    if (typeof cart.gift_card_total === 'number') {
      discTotal -= cart.gift_card_total;
      discUnits -= cart.gift_card_units || 0;
    } else {
      (cart.items || []).forEach(function (item) {
        if (!item.gift_card) return;
        discTotal -= item.original_line_price || 0;
        discUnits -= item.quantity || 0;
      });
    }
    var shipTotal = cart.total_price;
    // Solo tarjetas regalo: no hay nada que enviar, no se celebra el envío.
    // Mismo criterio que `snippets/roro-cart-reward.liquid`.
    var hasGoods = discTotal > 0;
    var discMet = DISC_MIN > 0 && discTotal >= DISC_MIN && discUnits >= DISC_MIN_ITEMS;

    // El primer `cart:update` tras añadir llega ANTES de que
    // `roro-cart-bundle-discount.js` pegue el código (~1 s después). Si el set
    // ya cumple y aún no hay rebaja, el envío se mide con lo que se va a pagar:
    // sin esto, un carrito de ₴5.000 (₴4.750 a pagar) celebraba «доставка
    // безкоштовна» que `ro.ro pay` no da (2026-10-08). Igual en la escalera.
    if (discMet && DISC_PCT > 0 && !(cart.total_discount > 0)) {
      shipTotal -= Math.round((discTotal * DISC_PCT) / 100);
    }

    var next = {
      disc: discMet,
      ship: hasGoods && SHIP_MIN > 0 && shipTotal >= SHIP_MIN
    };

    var gained = [];
    if (next.disc && !met.disc) gained.push({ key: 'disc', min: DISC_MIN });
    if (next.ship && !met.ship) gained.push({ key: 'ship', min: SHIP_MIN });

    // Se actualiza SIEMPRE, también cuando el carrito baja: si vuelve a subir,
    // vuelve a haber noticia. Y si sube de golpe cruzando los dos peldaños se
    // anuncia solo el más alto — dos avisos encadenados son ruido, y el pequeño
    // ya se ve cumplido en la escalera.
    met = next;

    if (!gained.length) return;
    gained.sort(function (a, b) {
      return b.min - a.min;
    });
    show(gained[0].key);
  }

  /**
   * El carrito completo, sin pedirlo si ya viene en el evento.
   *
   * Casi todos los emisores del tema mandan el carrito entero en
   * `detail.resource` (`rules/liquid-patterns.md`). El `fetch` es la red de
   * seguridad para los que no, no el camino normal: `roro-cart-bundle-discount.js`
   * ya pide `/cart.js` en cada `cart:update` y no hace falta duplicarlo.
   *
   * @param {Event} event
   */
  function readCart(event) {
    var resource = event && event.detail && event.detail.resource;
    if (resource && typeof resource.total_price === 'number' && typeof resource.item_count === 'number') {
      return Promise.resolve(resource);
    }
    // `RoroCart.get` ya devuelve `null` en vez de lanzar cuando la respuesta no
    // es JSON: esto es un adorno, no una funcion critica. Sin red el carrito
    // sigue funcionando y la escalera sigue contando la verdad.
    return window.RoroCart.get('/cart.js');
  }

  document.addEventListener('cart:update', function (event) {
    var data = (event && event.detail && event.detail.data) || {};
    if (data.didError) return;

    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      settleTimer = null;
      if (busy) return;
      busy = true;
      readCart(event)
        .then(evaluate)
        .catch(function () {})
        .then(function () {
          busy = false;
        });
    }, SETTLE_MS);
  });

  /* ==================================================================== */
  /* Salidas                                                              */
  /* ==================================================================== */

  if (ctaEl) {
    ctaEl.addEventListener('click', function () {
      hide();
      var drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.href = ctaEl.getAttribute('data-roro-reward-href') || '/cart';
    });
  }

  // Si un diálogo se abre con la nube de página ya puesta, la nube queda detrás
  // del top layer y no se ve. La decisión de dónde pintarla NO puede ser un
  // disparo único: `assets/dialog.js` hace el `showModal()` dentro de un
  // `requestAnimationFrame`, y ese frame puede tardar mucho más que `SETTLE_MS`
  // —en una pestaña que no está componiendo no llega nunca hasta que vuelve a
  // pintarse—. Medido en local: el aviso salía en modo página y el cajón se
  // abría después, tapándolo.
  //
  // Así que no se esconde: se MUDA. Si el que se ha abierto es el cajón del
  // carrito, la nube se va dentro y sigue su cuenta atrás donde toca. Si es otro
  // diálogo (buscador, quick add), ahí sí no hay sitio y se retira.
  //
  // Fase de CAPTURA: `DialogOpenEvent` se emite sin `bubbles`, y un evento que
  // no burbujea sigue bajando por la captura hasta su destino. Es el mismo
  // motivo por el que `assets/roro-cart-drawer.js` escucha `dialog:open` así.
  document.addEventListener(
    'dialog:open',
    function () {
      if (toast.hidden) return;
      if (toast.getAttribute('data-roro-reward-place') !== 'page') return;

      var dialog = drawerDialog();
      if (dialog && dialog.open) {
        placeInDrawer(dialog);
        return;
      }

      // NO se esconde a la primera. `DialogOpenEvent` se emite justo detras de
      // `showModal()` dentro de un `requestAnimationFrame` (`assets/dialog.js`),
      // y en un movil lento ese fotograma puede llegar con el `open` del
      // `<dialog>` todavia sin reflejar. Escondiendo aqui de golpe, el aviso
      // del 5% / envio gratis desaparecia ~200ms despues de salir: aparecia y
      // se iba, que es justo el «no me salia» del 2026-09-08.
      //
      // Se vuelve a mirar en el siguiente fotograma y una vez mas un poco
      // despues. Si para entonces el cajon sigue sin estar abierto es que el
      // dialogo que se ha abierto es OTRO (buscador, quick add), y ahi si:
      // detras del top layer el aviso no se ve, asi que se retira.
      var recheck = function (thenHide) {
        return function () {
          if (toast.hidden) return;
          if (toast.getAttribute('data-roro-reward-place') !== 'page') return;

          var later = drawerDialog();
          if (later && later.open) placeInDrawer(later);
          else if (thenHide) hide();
        };
      };

      requestAnimationFrame(recheck(false));
      setTimeout(recheck(true), 260);
    },
    true
  );

  // El aviso se va con el cajón: dentro de él no tiene vida propia.
  document.addEventListener(
    'dialog:close',
    function () {
      if (toast.getAttribute('data-roro-reward-place') === 'drawer') hide();
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') hide();
  });
})();
